import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';

import { Transition } from '@uirouter/core';
import { HelpWidgetFactory } from 'src/app/ajs-upgraded-providers';

import { BillingUtilsService } from 'src/app/billing/services/billing-utils.service';
import { SubscriptionService } from 'src/app/billing/services/subscription.service';
import { AiCreditsService } from 'src/app/settings/services/ai-credits.service';

import {
  AI_CREDITS_MIN_QUANTITY,
  AI_CREDITS_QUANTITY_STEP,
} from 'src/app/components/plans/plans.service';
import { UpdateSubscriptionService } from '../../services/update-subscription.service';

@Component({
  selector: 'update-subscription',
  templateUrl: './update-subscription.component.html',
  styleUrls: ['./update-subscription.component.scss'],
  standalone: false,
})
export class UpdateSubscriptionComponent implements OnInit {
  @Input() redirectTo;

  billingPeriod = 'annually';

  couponCode = null;
  addCoupon;

  purchaseAction;

  creditsTotal = 0;

  readonly quantityStep = AI_CREDITS_QUANTITY_STEP;
  readonly quantityMin = AI_CREDITS_MIN_QUANTITY;

  @ViewChild('purchaseLicensesForm')
  public purchaseLicensesForm: NgForm;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    public updateSubscriptionService: UpdateSubscriptionService,
    public subscriptionService: SubscriptionService,
    public billingUtils: BillingUtilsService,
    private transition: Transition,
    public helpWidgetFactory: HelpWidgetFactory,
    private aiCreditsService: AiCreditsService,
  ) {}

  ngOnInit(): void {
    this.purchaseAction = this.transition.params().purchaseAction;

    this.updateSubscriptionService.init(
      this.purchaseAction,
      this.transition.params().productCode,
      this.transition.params().monthly,
    );

    if (this.isAiCreditsAction) {
      void this.aiCreditsService
        .getAICredits()
        .then((credits) => {
          this.creditsTotal = credits.planSubscriptionCreditsTotal;
        })
        .catch(() => {
          this.creditsTotal = 0;
        })
        .finally(() => {
          this.detectChanges();
        });
    }
  }

  get isAiCreditsAction(): boolean {
    return (
      this.purchaseAction === 'add-additional-ai-credits' ||
      this.purchaseAction === 'remove-additional-ai-credits'
    );
  }

  get isAddingAiCredits(): boolean {
    return this.purchaseAction === 'add-additional-ai-credits';
  }

  getAiCreditsPurchase() {
    return this.updateSubscriptionService.displayLicensePurchase;
  }

  getAiCreditsQuantity(): number {
    const purchase = this.getAiCreditsPurchase();
    return this.isAddingAiCredits
      ? purchase?.licensesToAdd || 0
      : purchase?.licensesToRemove || 0;
  }

  getCurrentAdditionalMonthlyCredits(): number {
    return this.updateSubscriptionService.getCurrentAiPlanQuantity(
      this.getAiCreditsPurchase(),
    );
  }

  getTotalAdditionalMonthlyCredits(): number {
    return this.updateSubscriptionService.getCurrentAICreditsCount(
      this.getAiCreditsPurchase(),
    );
  }

  getNewAiCreditsTotal(): number {
    return (
      this.updateSubscriptionService.getCurrentAICreditsCount(
        this.getAiCreditsPurchase(),
      ) + this.creditsTotal
    );
  }

  getAiCreditsRemoveMax(): number {
    const current = this.getCurrentAdditionalMonthlyCredits();
    return Math.max(this.quantityMin, current - this.quantityStep);
  }

  onAiCreditsQuantityChange(): void {
    this.detectChanges();
    void this.getEstimate();
  }

  detectChanges() {
    this.changeDetectorRef.detectChanges();
  }

  _isFormValid() {
    var form = this.purchaseLicensesForm;
    return !form || form.valid;
  }

  applyCouponCode() {
    if (!this._isFormValid()) {
      return;
    }

    if (this.couponCode) {
      this.updateSubscriptionService.couponCode = this.couponCode;

      this.updateSubscriptionService.getEstimate().then(() => {
        if (!this.updateSubscriptionService.apiError) {
          this.addCoupon = false;
        }
      });
    }
  }

  clearCouponCode() {
    this.couponCode = null;
    this.updateSubscriptionService.couponCode = null;
    this.addCoupon = false;

    if (this.updateSubscriptionService.apiError) {
      this.updateSubscriptionService.getEstimate();
    }
  }

  getEstimate() {
    if (!this._isFormValid()) {
      return;
    }

    return this.updateSubscriptionService.getEstimate();
  }

  completePayment() {
    if (!this._isFormValid()) {
      return;
    }

    return this.updateSubscriptionService
      .preparePayment()
      .then(() => this.updateSubscriptionService.completePayment())
      .then(() => {
        this.updateSubscriptionService.purchases.forEach((purchase) => {
          const subscriptionItem = this.subscriptionService.items.find(
            (item) => item.subscription.id === purchase.subscriptionId,
          );
          if (subscriptionItem) {
            subscriptionItem.subscription.has_scheduled_changes =
              this.purchaseAction === 'remove' ||
              this.purchaseAction === 'remove-additional-ai-credits' ||
              this.purchaseAction === 'remove-interactive';
            subscriptionItem.subscription.changed_plan_id = undefined;
            subscriptionItem.subscription.changed_plan_quantity =
              this.purchaseAction === 'remove' ||
              this.purchaseAction === 'remove-additional-ai-credits' ||
              this.purchaseAction === 'remove-interactive'
                ? purchase.licensesToRemove
                : undefined;
          }
        });
      });
  }

  close() {
    if (this.redirectTo) {
      this.transition.router.stateService.go(
        this.transition.from(),
        this.transition.params('from'),
      );
    } else {
      const subscriptionIds = this.transition
        .params()
        .subscriptionId.split(',');
      this.transition.router.stateService.go('apps.billing.subscription', {
        subscriptionId: subscriptionIds[0],
      });
    }
  }

  getLicensesToAddContext() {
    const labelMap = {
      add: 'Number of display licenses you want to add',
      'add-additional-ai-credits': 'Number of AI credits you want to add',
      'add-interactive': 'Number of interactive licenses you want to add',
    };
    const isInteractive = this.purchaseAction === 'add-interactive';
    const purchase = isInteractive
      ? this.updateSubscriptionService.interactiveLicensePurchase
      : this.updateSubscriptionService.displayLicensePurchase;
    const isAddAction = this.purchaseAction === 'add';

    return {
      $implicit: {
        label: labelMap[this.purchaseAction],
        purchase,
        fieldName: 'licensesToAdd',
        minValue:
          isAddAction && !this.updateSubscriptionService.allowMultiple ? 1 : 0,
        maxValue: 10000,
        showError: purchase.licensesToAdd !== undefined,
      },
    };
  }

  getLicensesToRemoveContext() {
    const labelMap = {
      remove: 'Number of display licenses you want to remove',
      'remove-additional-ai-credits': 'Number of AI credits you want to remove',
      'remove-interactive': 'Number of interactive licenses you want to remove',
    };
    const isInteractive = this.purchaseAction === 'remove-interactive';
    const purchase = isInteractive
      ? this.updateSubscriptionService.interactiveLicensePurchase
      : this.updateSubscriptionService.displayLicensePurchase;
    const currentCount =
      this.updateSubscriptionService.getCurrentDisplayCount(purchase) || 1;

    return {
      $implicit: {
        label: labelMap[this.purchaseAction],
        purchase,
        fieldName: 'licensesToRemove',
        minValue: 1,
        maxValue: Math.max(1, currentCount - 1),
      },
    };
  }

  get planLabel() {
    return {
      add: 'display licenses',
      'add-additional-ai-credits': 'AI credits',
      'add-interactive': 'interactive licenses',
      remove: 'display licenses',
      'remove-additional-ai-credits': 'AI credits',
      'remove-interactive': 'interactive licenses',
      unlimited: 'unlimited licenses',
    };
  }
}
